'use client';

import { PublicPageLayout } from '@letsgo/PublicPageLayout';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Alert, Box, Container, Typography, Link as MuiLink, Paper } from '@mui/material';
import { PRIVACY_EMAIL, SUPPORT_EMAIL } from '@/lib/site-contact';

const SECTION_IDS = {
  dataWeCollect: 'data-we-collect',
  yourRights: 'your-rights',
  legalBases: 'legal-bases',
  subprocessors: 'subprocessors',
  contact: 'contact',
} as const;

export default function GDPRPage() {
  const [headings, setHeadings] = useState<{ id: string; text: string }[]>([]);

  useEffect(() => {
    const extractHeadings = () => {
      const headingElements = Array.from(document.querySelectorAll('#gdpr-content h2'));
      const extracted = headingElements.map((heading, index) => {
        const id = heading.id || `section-${index}`;
        return { id, text: heading.textContent || '' };
      });
      setHeadings(extracted);
    };
    extractHeadings();
  }, []);

  const lastReviewed = 'March 17, 2026';

  return (
    <PublicPageLayout>
      <Container maxWidth="lg" sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, py: 4, gap: 4 }}>
        <Box sx={{ flexGrow: 1, pr: { xs: 0, md: 3 } }} id="gdpr-content">
          <Typography variant="h4" component="h1" fontWeight={700} mb={2} color="text.primary">
            GDPR Compliance
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" mb={4}>
            Last reviewed: {lastReviewed}. For full details, see our{' '}
            <MuiLink component={Link} href="/privacy" color="primary">Privacy Policy</MuiLink>.
          </Typography>

          <Typography component="h2" id={SECTION_IDS.dataWeCollect} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
            1. What data we collect and why
          </Typography>
          <Typography variant="body1" paragraph>
            We collect only what is necessary to provide and improve FloAI. This includes: account information (email, name, profile details), usage data (how you use the product), and data you provide when connecting social accounts or using AI features. We use this to deliver the service, support you, improve our product, and comply with legal obligations. For a complete list and purposes, see our Privacy Policy.
          </Typography>

          <Typography component="h2" id={SECTION_IDS.yourRights} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
            2. Your rights under GDPR
          </Typography>
          <Typography variant="body1" paragraph>
            If you are in the European Economic Area (EEA) or UK, you have the following rights regarding your personal data:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><strong>Access</strong> — You can request a copy of the personal data we hold about you.</li>
            <li><strong>Rectification</strong> — You can ask us to correct inaccurate or incomplete data.</li>
            <li><strong>Erasure</strong> — You can request deletion of your personal data, subject to legal exceptions.</li>
            <li><strong>Portability</strong> — You can request your data in a structured, machine-readable format.</li>
            <li><strong>Restriction</strong> — You can ask us to limit how we process your data in certain circumstances.</li>
            <li><strong>Objection</strong> — You can object to processing based on legitimate interests or for direct marketing.</li>
            <li><strong>Withdraw consent</strong> — Where we rely on consent, you can withdraw it at any time.</li>
          </Box>
          <Typography variant="body1" paragraph>
            To exercise any of these rights, contact us using the details in the Contact section below. We will respond within the timeframes required by law (typically one month). You also have the right to lodge a complaint with a supervisory authority in your country.
          </Typography>

          <Typography component="h2" id={SECTION_IDS.legalBases} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
            3. Legal bases for processing
          </Typography>
          <Typography variant="body1" paragraph>
            We process your personal data on the following bases:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><strong>Contract</strong> — To provide the service you signed up for (account, features, support).</li>
            <li><strong>Consent</strong> — Where you have given clear consent (e.g. marketing, optional features, cookies where required).</li>
            <li><strong>Legitimate interest</strong> — To operate and improve our service, prevent fraud, and ensure security, where our interests are balanced with your rights.</li>
            <li><strong>Legal obligation</strong> — Where we must retain or disclose data to comply with law.</li>
          </Box>

          <Typography component="h2" id={SECTION_IDS.subprocessors} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
            4. Subprocessors and data transfers
          </Typography>
          <Typography variant="body1" paragraph>
            We use service providers (subprocessors) to run FloAI. These may include: hosting and infrastructure (e.g. Vercel), database and storage, email and notifications, and analytics or monitoring. We choose providers that offer appropriate safeguards. When data is transferred outside the EEA/UK, we rely on adequacy decisions, standard contractual clauses, or other approved mechanisms to ensure an adequate level of protection. A list of key subprocessors and their purposes is available in our Privacy Policy; we can provide more detail on request.
          </Typography>

          <Typography component="h2" id={SECTION_IDS.contact} variant="h5" fontWeight={700} mt={4} mb={2} color="text.primary">
            5. Contact (privacy and GDPR)
          </Typography>
          <Typography variant="body1" paragraph>
            For any questions about this page, your personal data, or to exercise your rights, contact us:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>
              Email:{' '}
              <MuiLink href={`mailto:${PRIVACY_EMAIL}`} color="primary">
                {PRIVACY_EMAIL}
              </MuiLink>{' '}
              or{' '}
              <MuiLink href={`mailto:${SUPPORT_EMAIL}`} color="primary">
                {SUPPORT_EMAIL}
              </MuiLink>{' '}
              (use &quot;GDPR&quot; in the subject for faster routing).
            </li>
            <li>Contact form: <MuiLink component={Link} href="/contact" color="primary">/contact</MuiLink> — select a category and mention GDPR or data rights in your message.</li>
          </Box>
          <Typography variant="body2" color="text.secondary">
            We will respond to GDPR-related requests within the timeframe required by applicable law.
          </Typography>

          <Alert severity="info" sx={{ mt: 4 }}>
            <Typography variant="body2" component="div">
              <strong>Not legal advice:</strong> This page is an overview for transparency. It does not replace our{' '}
              <MuiLink component={Link} href="/privacy" color="inherit" underline="always">
                Privacy Policy
              </MuiLink>{' '}
              or qualified legal review. Obtain counsel where your situation requires it.
            </Typography>
          </Alert>
        </Box>

        <Box sx={{ width: { xs: '100%', md: 250 }, flexShrink: 0 }}>
          <Paper elevation={3} sx={{ position: 'sticky', top: 100, p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              On this page
            </Typography>
            <Box component="nav">
              {headings.map((heading) => (
                <MuiLink
                  key={heading.id}
                  href={`#${heading.id}`}
                  sx={{
                    display: 'block',
                    py: 0.5,
                    pl: 1,
                    textDecoration: 'none',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                    borderLeft: '2px solid transparent',
                  }}
                >
                  {heading.text}
                </MuiLink>
              ))}
            </Box>
          </Paper>
        </Box>
      </Container>
    </PublicPageLayout>
  );
}
